/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { IconField } from 'primereact/iconfield';
import { InputText } from 'primereact/inputtext';
import { useOnOutsideClick } from '@/hooks/useClickOutside.tsx';
import { useDocLoader } from '@/pages/DocsPage/useDocLoader';
import MiniSearch, { SearchResult } from 'minisearch';
import removeMd from 'remove-markdown';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { Divider } from 'primereact/divider';
import { CNavLink } from '@/components';
import { AppRoutes } from '@/constants/routes.ts';
import { InputIcon } from 'primereact/inputicon';
import { useLocation } from 'react-router';
import { cleanedPath } from '@/pages/DocsPage/shared-utils.ts';

interface Props {
  docWrapper: string;
  onSelectResult: () => void;
  visible: boolean;
  setVisible: (v: boolean) => void;
  searchResult: SearchResult[];
  setSearchResult: (s: SearchResult[]) => void;
}

const markdownToText = (content: string) => {
  const length = 250;
  let text = removeMd(content);
  const pattern = /^\s*import\s+.*?\s+from\s+(['"]).*?\1;?\s*$/gm;
  try {
    text = text.replace(pattern, '');
  } catch (e) {
    console.error(e);
  }
  return text.length > length ? text.slice(0, length).concat('...') : text;
};

export const DocModalSearch: React.FC<Props> = ({ visible, setVisible, searchResult, setSearchResult, ...props }) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const { manifest } = useDocLoader();
  const { pathname } = useLocation();

  const { innerBorderRef } = useOnOutsideClick(() => setVisible(false));
  const miniSearch = new MiniSearch({
    fields: ['title', 'keywords', 'content'],
    storeFields: ['title', 'content', 'slug']
  });

  miniSearch.addAll(manifest.map((el, index) => ({ ...el, id: index })));

  const handleOnSearch = (value: string) => {
    setSearchValue(value);
    const result = miniSearch.search(value, { prefix: true, boost: { content: 2 } });
    if (result.length > 0) {
      setSearchResult(result);
    }
  };

  const handleSelectResult = (searchResult: SearchResult) => {
    const path = cleanedPath(pathname);
    if (path === searchResult.slug) {
      props.onSelectResult();
    }
    setVisible(false);
  };

  return (
    <Dialog
      closable
      position="top"
      resizable={false}
      showHeader={false}
      pt={{ content: { className: 'p-0', style: { backgroundColor: '' } } }}
      visible={visible}
      onHide={() => {
        setVisible(false);
      }}
      style={{ width: '50%' }}
    >
      <div className="flex flex-column" ref={innerBorderRef}>
        <div className="p-inputgroup flex-1 w-full">
          <IconField iconPosition="right" className="w-full">
            <InputIcon className=""> ESC </InputIcon>
            <InputText
              id="searchField"
              value={searchValue}
              onChange={(e) => handleOnSearch(e.target.value)}
              placeholder="Search here ..."
              className="w-full border-noround"
            />
          </IconField>
          {searchResult.length > 0 && (
            <Button
              onClick={() => {
                setSearchResult([]);
                setSearchValue('');
              }}
              size="small"
              className="text-xs border-noround"
              severity="contrast"
              icon="pi pi-trash"
              iconPos="right"
              label={t(['actions.clear', 'Clear'])}
            />
          )}
        </div>
        {searchResult.length > 0 && (
          <div className="px-2 surface-card pt-1 overflow-y-auto h-fit" style={{ minHeight: '16rem' }}>
            {searchResult.map((rs, index) => {
              return (
                <CNavLink
                  onClick={() => handleSelectResult(rs)}
                  to={AppRoutes.docs.path.concat(`/${rs.slug}`)}
                  key={index}
                >
                  <div className="w-full mb-2 cursor-pointer hover:bg-primary p-1 transition-duration-100 transition-colors">
                    <span className="block font-medium my-1">{rs.title}</span>
                    <span className="block font-light text-sm">{markdownToText(rs.content)}</span>
                  </div>
                  <Divider pt={{ root: { style: { margin: '0' } } }} />
                </CNavLink>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
};
